import { CommonUtils } from '../../(common-lib)/CommonUtils';
import { EmailIds, EmailTemplates2 } from '../../(common-lib)/EmailTemplates2';
import { LoanData, LoanStatus, USER_ROLES, UserRoleType } from '../../(common-lib)/LoanData';
import { buildOverviewSignInUrl, formatPropertyAddress } from '../../(common-lib)/MailUtils';
import { isBrokerOnlyLoan } from '../../(common-lib)/ParticipantMode';
import { sendSignInLink } from '../../(ui-lib)/InviteUser';
import { MailClient } from '../../(ui-lib)/MailClient';

// In Loan Terms and Loan Adjustments pages,
//
// - display the Invite Client to Apply button if showInviteClientToApply returns true
// - send mail on clicking Invite Client to Apply button
//
// - display the Notify Client button if showNotifyClient returns true
// - send mail on clicking Notify Client button
export abstract class BaseLoanTermsMailHandler {
  protected scenario: string;
  protected role: UserRoleType;
  protected loanData: LoanData | undefined;

  constructor(scenario: string, role: UserRoleType, loanData: LoanData | undefined) {
    this.scenario = scenario;
    this.role = role;
    this.loanData = loanData;
  }

  // Invite Client to Apply button - show and send mail 
  showInviteClientToApply(): boolean {
    if (!this.loanData) return false;
    // Fast-track loans skip Draft, so the client still needs an invite in Processing.
    if (this.loanData.loanStatus !== LoanStatus.DRAFT && !this.loanData.fastTrack) return false;
    return true;
  }

  async sendInviteClientToApplyMail(): Promise<void> {
  }

  // Notify Client button - show and send mail 
  showNotifyClient(): boolean {
    if (!this.loanData) return false;
    if (this.loanData.loanStatus === LoanStatus.DRAFT) return false;
    return true;
  }

  protected getNotifyMailDetails(_additionalCCEmail?: string): {toEmail: string, recipientFirstName: string, ccEmail?: string} {
    return {toEmail: '', recipientFirstName: '', ccEmail: ''};
  }

  async sendLoanTermsAdjustedMail(additionalCCEmail?: string): Promise<void> {
    const { toEmail, recipientFirstName, ccEmail } = this.getNotifyMailDetails(additionalCCEmail);

    if (!toEmail) return;

    const ccEmailList: string[] = [getLoanOfficerEmail(this.loanData)];
    if (ccEmail) {
      ccEmailList.push(ccEmail);
    }
    const loanOfficerName =  this.loanData?.loanOfficer?.name?.trim() || '';
    
    const url = buildOverviewSignInUrl(this.loanData?.crmData?.uniqueLoanId, this.loanData?.loanStatus);
    const templateData = {
      recipientFirstName: recipientFirstName,
      loanOfficerName,
      propertyAddress: getPropertyAddress(this.loanData),
      url,
    };
    await MailClient.sendTemplateEmailV2(
      EmailIds.LOANS_EMAIL,
      toEmail,
      EmailTemplates2.LOAN_TERMS_UPDATED_AVAILABLE_TO_CLIENT,
      templateData,
      ccEmailList,
      [],
    ); 
  }

  async sendApplicationCancelledMail(_notifyBroker: boolean, _notifyBorrower: boolean): Promise<void> {
  }
}

// Gets who initiated the loan. If loan officer initiated the loan, then also gets on whose behalf the loan was initiated.
function getLoanInitiationScenario(loanData: LoanData | undefined): 'INVALID' | 'LOAN_BY_BORROWER' | 'LOAN_BY_BROKER' | 'LOAN_BY_LO_FOR_BORROWER' | 'LOAN_BY_LO_FOR_BROKER' {
  if (!loanData) return 'INVALID';
  if (loanData?.userType === 'borrower') return 'LOAN_BY_BORROWER';
  if (loanData?.userType === 'broker') return 'LOAN_BY_BROKER';
  if (loanData?.userType === 'loan_officer') {
    return loanData?.brokerInfo?.email ? 'LOAN_BY_LO_FOR_BROKER' : 'LOAN_BY_LO_FOR_BORROWER';
  }
  return 'INVALID';
}

export function getLoanTermsMailHandler(role: UserRoleType | undefined, loanData: LoanData | undefined): BaseLoanTermsMailHandler{
  const scenario = getLoanInitiationScenario(loanData);
  // Based on the logged in user role, call the respective handler
  switch (role) {
    case USER_ROLES.BROKER:
      return new BrokerLoanTermsMailHandler(scenario, loanData);
    case USER_ROLES.LOAN_OFFICER:
      return new LoanOfficerLoanTermsMailHandler(scenario, loanData);
    case USER_ROLES.MANAGER:
      return new ManagerLoanTermsMailHandler(scenario, loanData);
    case USER_ROLES.PROCESSOR:
      return new ProcessorLoanTermsMailHandler(scenario, loanData);
    case USER_ROLES.BORROWER:
      return new BorrowerLoanTermsMailHandler(scenario, loanData);
    case USER_ROLES.BORROWER_VIA_BROKER:
      return new BorrowerViaBrokerLoanTermsMailHandler(scenario, loanData);
    case USER_ROLES.UNDERWRITER:
    default:
      return new InvalidLoanTermsMailHandler(scenario, role || USER_ROLES.BORROWER, loanData);
  }
}

function getCancellingApplicantDetails(role: UserRoleType, loanData: LoanData | undefined): {
  applicantFirstName: string;
  applicantLastName: string;
} {
  if (role === USER_ROLES.BROKER) {
    return {
      applicantFirstName: (loanData?.brokerInfo?.firstName || '').trim() || 'Broker',
      applicantLastName: (loanData?.brokerInfo?.lastName || '').trim(),
    };
  }

  return {
    applicantFirstName: (loanData?.contactInfo?.firstName || '').trim() || 'Borrower',
    applicantLastName: (loanData?.contactInfo?.lastName || '').trim(),
  };
}

function getBorrowerEmail(loanData: LoanData | undefined) {
  return (loanData?.contactInfo?.email || '').trim();
}

function getBrokerEmail(loanData: LoanData | undefined) {
  return (loanData?.brokerInfo?.email || '').trim();
}

function getLoanOfficerEmail(loanData: LoanData | undefined): string {
  return (loanData?.loanOfficer?.email || '').trim();
}

function getPropertyAddress(loanData: LoanData | undefined): string {
  return formatPropertyAddress(loanData?.propertyAddress, loanData?.propertyState?.key);
}

export class InvalidLoanTermsMailHandler extends BaseLoanTermsMailHandler {
  constructor(scenario: string, role: UserRoleType, loanData: LoanData | undefined) {
    super(scenario, role, loanData);
  }
  
  showInviteClientToApply(): boolean {
    return false;
  }

  showNotifyClient(): boolean {
    return false;
  }
}

export class BrokerLoanTermsMailHandler extends BaseLoanTermsMailHandler {
  constructor(scenario: string, loanData: LoanData | undefined) {
    super(scenario, USER_ROLES.BROKER, loanData);
  }

  // Show Invite Client to Apply only if the borrower email is available
  showInviteClientToApply(): boolean {
    if (isBrokerOnlyLoan(this.loanData)) return false;

    const canInvite = super.showInviteClientToApply();
    if (!canInvite) return false;
    // TODO-Sree-P1: Broker should have done Submit For Review
    // Even if Save As Draft is done, curently the Invite For Client is being shown
    return !!this.loanData?.contactInfo?.email;
  }

  showNotifyClient(): boolean {
    if (isBrokerOnlyLoan(this.loanData)) return false;

    return super.showNotifyClient();
  }

  async sendInviteClientToApplyMail(): Promise<void> {
    try {
      const uniqueLoanId = this.loanData?.crmData?.uniqueLoanId || '';
      const toEmail = getBorrowerEmail(this.loanData);

      if (!toEmail) return;
      if (!uniqueLoanId) return;

      await sendSignInLink(toEmail, uniqueLoanId, 'broker_invites_bvb');
    } catch (error) {
      CommonUtils.logError('Failed to invite client to apply:', error);
    }
  }

  protected getNotifyMailDetails(additionalCCEmail: string): {toEmail: string, recipientFirstName: string, ccEmail?: string} {
    if (!this.showNotifyClient()) return {toEmail: '', recipientFirstName: ''};

    let toEmail: string = '';
    let recipientFirstName: string = '';
    let ccEmail: string = '';
    
    const scenario = getLoanInitiationScenario(this.loanData);
    switch (scenario) {
      case 'LOAN_BY_BROKER':
      case 'LOAN_BY_LO_FOR_BROKER':
        toEmail = getBorrowerEmail(this.loanData);
        recipientFirstName = (this.loanData?.contactInfo?.firstName || '').trim();
        ccEmail = additionalCCEmail;
        break;
      default: break;
    }
    
    return {toEmail, recipientFirstName, ccEmail};
  }

  async sendApplicationCancelledMail(_notifyBroker: boolean, _notifyBorrower: boolean): Promise<void> {
    const staffEmail = getLoanOfficerEmail(this.loanData);
    if (!staffEmail) return;

    const { staffFirstName, staffLastName, propertyAddress } = getApplicationCancelledTemplateData(this.loanData);
    const { applicantFirstName, applicantLastName } = getCancellingApplicantDetails(this.role, this.loanData);

    await MailClient.sendTemplateEmailV2(
      EmailIds.LOANS_EMAIL,
      staffEmail,
      EmailTemplates2.LOAN_APPLICATION_CANCELLED_TO_STAFF,
      {
        staffFirstName: staffFirstName || 'there',
        staffLastName,
        propertyAddress,
        applicantFirstName,
        applicantLastName,
      },
      [],
      [],
    );
  }
}

export class BorrowerLoanTermsMailHandler extends BaseLoanTermsMailHandler {
  constructor(scenario: string, loanData: LoanData | undefined) {
    super(scenario, USER_ROLES.BORROWER, loanData);
  }

  showInviteClientToApply(): boolean {
    return false;
  }

  showNotifyClient(): boolean {
    return false;
  }

  async sendApplicationCancelledMail(_notifyBroker: boolean, _notifyBorrower: boolean): Promise<void> {
    const staffEmail = getLoanOfficerEmail(this.loanData);
    if (!staffEmail) return;

    const { staffFirstName, staffLastName, propertyAddress } = getApplicationCancelledTemplateData(this.loanData);
    const { applicantFirstName, applicantLastName } = getCancellingApplicantDetails(this.role, this.loanData);

    await MailClient.sendTemplateEmailV2(
      EmailIds.LOANS_EMAIL,
      staffEmail,
      EmailTemplates2.LOAN_APPLICATION_CANCELLED_TO_STAFF,
      {
        staffFirstName: staffFirstName || 'there',
        staffLastName,
        propertyAddress,
        applicantFirstName,
        applicantLastName,
      },
      [],
      [],
    );
  }
}

export class BorrowerViaBrokerLoanTermsMailHandler extends BaseLoanTermsMailHandler {
  constructor(scenario: string, loanData: LoanData | undefined) {
    super(scenario, USER_ROLES.BORROWER_VIA_BROKER, loanData);
  }

  showInviteClientToApply(): boolean {
    return false;
  }

  showNotifyClient(): boolean {
    return false;
  }
}

export abstract class StaffLoanTermsMailHandler extends BaseLoanTermsMailHandler {
  showInviteClientToApply(): boolean {
    const canInvite = super.showInviteClientToApply();
    if (!canInvite) return false;

    return true;
  }

  async sendInviteClientToApplyMail(): Promise<void> {
    try {
      if (!this.showInviteClientToApply()) return;

      let toEmail: string = '';
      let origin: string = '';

      const scenario = getLoanInitiationScenario(this.loanData);
      switch (scenario) {
        case 'LOAN_BY_BORROWER':
        case 'LOAN_BY_LO_FOR_BORROWER':
          toEmail = getBorrowerEmail(this.loanData);
          origin = 'loanofficer_invites_borrower';
          break;
        case 'LOAN_BY_BROKER':
        case 'LOAN_BY_LO_FOR_BROKER':
          toEmail = getBrokerEmail(this.loanData);
          origin = 'loanofficer_invites_broker';
          break;
        default:
          break;
      }

      const uniqueLoanId = this.loanData?.crmData?.uniqueLoanId || '';

      if (!toEmail) return;
      if (!origin) return;
      if (!uniqueLoanId) return;

      // For LO-initiated loans, land the invited user directly on overview after sign-in.
      const callbackTarget = (scenario === 'LOAN_BY_LO_FOR_BORROWER' || scenario === 'LOAN_BY_LO_FOR_BROKER')
        ? `/loanapp/overview?loanid=${encodeURIComponent(uniqueLoanId)}`
        : uniqueLoanId;

      await sendSignInLink(toEmail, callbackTarget, origin);
    } catch (error) {
      CommonUtils.logError('Failed to invite client to apply:', error);
    }
  }

  protected getNotifyMailDetails(_additionalCCEmail: string): {toEmail: string, recipientFirstName: string, ccEmail?: string} {
    if (!this.showNotifyClient()) return {toEmail: '', recipientFirstName: ''};

    let toEmail: string = '';
    let recipientFirstName: string = '';
    const ccEmail: string = '';

    const scenario = getLoanInitiationScenario(this.loanData);
    switch (scenario) {
      case 'LOAN_BY_BORROWER':
      case 'LOAN_BY_LO_FOR_BORROWER':
        toEmail = getBorrowerEmail(this.loanData);
        recipientFirstName = (this.loanData?.contactInfo?.firstName || '').trim();
        break;
      case 'LOAN_BY_BROKER':
      case 'LOAN_BY_LO_FOR_BROKER':
        toEmail = getBrokerEmail(this.loanData);
        recipientFirstName = (this.loanData?.brokerInfo?.firstName || '').trim();
        break;
      default:
        break;
    }
    return {toEmail, recipientFirstName, ccEmail};
  }

  async sendApplicationCancelledMail(notifyBroker: boolean, notifyBorrower: boolean): Promise<void> {
    if (!notifyBroker && !notifyBorrower) return;

    const { staffFirstName, staffLastName, propertyAddress } = getApplicationCancelledTemplateData(this.loanData);
    const brokerEmail = notifyBroker ? getBrokerEmail(this.loanData) : '';
    const borrowerEmail = notifyBorrower ? getBorrowerEmail(this.loanData) : '';
    const preferredRecipient = this.getNotifyMailDetails('').toEmail;

    let toEmail = '';
    let recipientFirstName = '';
    let secondaryEmail = '';

    if (brokerEmail && borrowerEmail) {
      if (preferredRecipient === brokerEmail) {
        toEmail = brokerEmail;
        recipientFirstName = (this.loanData?.brokerInfo?.firstName || '').trim() || 'there';
        secondaryEmail = borrowerEmail;
      } else {
        toEmail = borrowerEmail;
        recipientFirstName = (this.loanData?.contactInfo?.firstName || '').trim() || 'there';
        secondaryEmail = brokerEmail;
      }
    } else if (brokerEmail) {
      toEmail = brokerEmail;
      recipientFirstName = (this.loanData?.brokerInfo?.firstName || '').trim() || 'there';
    } else if (borrowerEmail) {
      toEmail = borrowerEmail;
      recipientFirstName = (this.loanData?.contactInfo?.firstName || '').trim() || 'there';
    }

    if (!toEmail) return;

    const ccEmailList = [getLoanOfficerEmail(this.loanData), secondaryEmail]
      .filter(Boolean)
      .filter((email, index, emails) => emails.indexOf(email) === index);

    await MailClient.sendTemplateEmailV2(
      EmailIds.LOANS_EMAIL,
      toEmail,
      EmailTemplates2.APPLICATION_DECLINED_BORROWER_NOTIFICATION,
      {
        recipientFirstName,
        staffFirstName,
        staffLastName,
        propertyAddress,
      },
      ccEmailList,
      [],
    );
  }
}

export class LoanOfficerLoanTermsMailHandler extends StaffLoanTermsMailHandler {
  constructor(scenario: string, loanData: LoanData | undefined) {
    super(scenario, USER_ROLES.LOAN_OFFICER, loanData);
  }
}

export class ManagerLoanTermsMailHandler extends StaffLoanTermsMailHandler {
  constructor(scenario: string, loanData: LoanData | undefined) {
    super(scenario, USER_ROLES.MANAGER, loanData);
  }

  protected getNotifyMailDetails(additionalCCEmail: string): {toEmail: string, recipientFirstName: string, ccEmail?: string} {
    const {toEmail, recipientFirstName } = super.getNotifyMailDetails(additionalCCEmail);
    return {toEmail, recipientFirstName, ccEmail: additionalCCEmail};
  }
}

export class ProcessorLoanTermsMailHandler extends StaffLoanTermsMailHandler {
  constructor(scenario: string, loanData: LoanData | undefined) {
    super(scenario, USER_ROLES.PROCESSOR, loanData);
  }
}

function getApplicationCancelledTemplateData(loanData: LoanData | undefined): {
  staffFirstName: string;
  staffLastName: string;
  propertyAddress: string;
} {
  const loanOfficerName = loanData?.loanOfficer?.name?.trim() || '';
  const [staffFirstName, ...staffLastNameParts] = loanOfficerName.split(/\s+/).filter(Boolean);
  const staffLastName = staffLastNameParts.join(' ');

  return {
    staffFirstName: (staffFirstName || '').trim(),
    staffLastName: (staffLastName || '').trim(),
    propertyAddress: getPropertyAddress(loanData) || 'your loan application',
  };
}
